import React from 'react';
import { X, Heart, ShoppingBag, ArrowRight, Check, Star } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useWishlist } from '../contexts/WishlistContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Quick View Modal - Preview product details without leaving the grid
 */
const QuickViewModal = ({ product, isOpen, onClose }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  
  if (!isOpen || !product) return null;

  const imageUrl = product.images?.[0]?.url || product.image || '';
  const fullImageUrl = imageUrl.startsWith('/') ? `${API_URL}${imageUrl}` : imageUrl;
  const inWishlist = isInWishlist(product.product_type || product.id);
  
  const href = product.has_configurator 
    ? `/products/${product.product_type}` 
    : (product.cta_link || `/products/${product.product_type}`);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="grid md:grid-cols-2 h-full">
            {/* Image Section */}
            <div className="relative aspect-square md:aspect-auto bg-neutral-100">
              {fullImageUrl ? (
                <img 
                  src={fullImageUrl}
                  alt={product.display_name || product.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-neutral-400">No image</span>
                </div>
              )}
              
              {/* Featured badge */}
              {product.is_featured && (
                <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> Featured
                </div>
              )}
            </div>

            {/* Content Section */}
            <div className="p-6 md:p-8 flex flex-col overflow-y-auto max-h-[50vh] md:max-h-full">
              {/* Category */}
              <span className="text-xs uppercase tracking-wider text-amber-600 font-medium">
                {product.category_slug?.replace('-', ' ') || 'Window Treatments'}
              </span>

              {/* Title */}
              <h2 className="text-2xl md:text-3xl font-serif text-neutral-900 mt-2">
                {product.display_name || product.title}
              </h2>

              {/* Price */}
              {(product.price_from || product.base_price) > 0 && (
                <p className="text-xl font-semibold text-amber-600 mt-2">
                  From ${product.price_from || product.base_price}
                </p>
              )}

              {/* Description */}
              <p className="text-neutral-600 mt-4 leading-relaxed">
                {product.short_description || product.description?.slice(0, 300)}
              </p>

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-neutral-900 mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {product.features.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-neutral-600">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{typeof feature === 'string' ? feature : (feature.title || feature.text || '')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Benefits */}
              {product.benefits && product.benefits.length > 0 && (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {product.benefits.slice(0, 3).map((benefit, idx) => (
                      <span 
                        key={idx} 
                        className="text-xs bg-neutral-100 text-neutral-700 px-3 py-1 rounded-full"
                      >
                        {typeof benefit === 'string' ? benefit : (benefit.title || benefit.text || '')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-auto pt-6 flex flex-col sm:flex-row gap-3">
                <Link to={href} className="flex-1" onClick={onClose}>
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    {product.has_configurator ? 'Configure Now' : 'View Details'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                
                <Button 
                  variant="outline" 
                  onClick={() => toggleWishlist(product)}
                  className={inWishlist ? 'border-red-300 text-red-500 hover:bg-red-50' : ''}
                >
                  <Heart className={`w-4 h-4 mr-2 ${inWishlist ? 'fill-current' : ''}`} />
                  {inWishlist ? 'Saved' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuickViewModal;
